<template>
  <div v-if="visible" class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('qvct_survey.exportReport.title') }}</h5>
          <button type="button" class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal" :disabled="busy" aria-label="Close">
            <span class="svg-icon svg-icon-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                      fill="currentColor" />
                <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                      fill="currentColor" />
              </svg>
            </span>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="!categories || categories.length === 0" class="text-center py-4">
            <img src="@/assets/media/pos/Empty2.png" alt="" class="mw-100 h-150px" />
            <div class="text-gray-400 fs-4 fw-bold">{{ $t('no_data_found') }}</div>
          </div>
          <div v-else>
            <div class="mb-4 border rounded p-3 bg-light">
              <h6 class="mb-3">{{ $t('qvct_survey.exportReport.globalCharts') }}</h6>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" checked disabled />
                <label class="form-check-label text-muted">{{ $t('qvct_survey.exportReport.responseRateChart') }}</label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" checked disabled />
                <label class="form-check-label text-muted">{{ $t('qvct_survey.exportReport.respondentFeedback') }}</label>
              </div>
            </div>


            <div v-for="cat in categories" :key="cat.id" class="mb-4 border rounded p-3">
              <h6 class="mb-3">{{ cat.name }}</h6>
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold">{{ $t('qvct_survey.exportReport.tables') }}</label>
                  <div class="form-check mb-2">
                    <input class="form-check-input" type="checkbox" v-model="selections[cat.id].indexTable"
                           :id="`idx-${cat.id}-index`" :disabled="busy" />
                    <label class="form-check-label" :for="`idx-${cat.id}-index`">
                      {{ $t('qvct_survey.exportReport.markerIndex') }}
                    </label>
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="form-label fw-bold">{{ $t('qvct_survey.exportReport.populationCharts') }}</label>
                  <div class="form-check mb-2">
                    <input class="form-check-input" type="checkbox" v-model="selections[cat.id].populationChart"
                           :id="`idx-${cat.id}-popchart`" :disabled="busy" />
                    <label class="form-check-label" :for="`idx-${cat.id}-popchart`">
                      {{ $t('qvct_survey.exportReport.populationChart') }}
                    </label>
                  </div>
                </div>

                <div class="col-md-4">
                  <label class="form-label fw-bold">{{ $t('qvct_survey.exportReport.comparisonCharts') }}</label>
                  <div class="form-check mb-2">
                    <input class="form-check-input" type="checkbox" v-model="selections[cat.id].subcategoriesChart"
                           :id="`idx-${cat.id}-subcat`" :disabled="busy" />
                    <label class="form-check-label" :for="`idx-${cat.id}-subcat`">
                      {{ $t('qvct_survey.exportReport.subcategoriesComparison') }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-md-6">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="selections[cat.id].radarOverview"
                           :id="`idx-${cat.id}-radar-overview`" :disabled="busy" />
                    <label class="form-check-label" :for="`idx-${cat.id}-radar-overview`">
                      {{ $t('qvct_survey.exportReport.radarOverview') }}
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="selections[cat.id].radar"
                           :id="`idx-${cat.id}-radar`" :disabled="busy" />
                    <label class="form-check-label" :for="`idx-${cat.id}-radar`">
                      {{ $t('qvct_survey.exportReport.radarCharts') }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <Button type="button" btnClass="btn py-2 btn-secondary" :btn-text="$t('button.close')"
                  @click="closeModal" :disabled="busy"/>
          <Button :is-loading="busy" type="button" btnClass="btn py-2 btn-primary"
                  :btn-text="$t('button.export')" @click="confirm" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, watch, onMounted } from 'vue'
import Button from "@/components/Button.vue";

const props = defineProps({
  visible: { type: Boolean, default: false },
  categories: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false }
})

const emits = defineEmits(['close', 'confirm'])

// Structure simplifiée qui correspond à ce qu'attend exportSelectedToWord
const selections = reactive({})

// Initialiser les sélections
const initializeSelections = () => {
  props.categories.forEach(cat => {
    if (!selections[cat.id]) {
      selections[cat.id] = {
        indexTable: true,
        populationChart: true,
        subcategoriesChart: true,
        radarOverview: true,
        radar: true
      }
    }
  })
}

// Fermer la modal
const closeModal = () => {
  if (!props.busy) {
    emits('close')
  }
}

// Confirmer l'export
const confirm = () => {
  const exportData = {
    selections: JSON.parse(JSON.stringify(selections))
  }
  emits('confirm', exportData)
}

// Surveiller les changements de catégories
watch(() => props.categories, (newCategories) => {
  initializeSelections()
}, { immediate: true })

// Réinitialiser quand la modal se ferme
watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    initializeSelections()
  }
})
</script>

<style scoped>
.modal {
  background: rgba(0,0,0,0.5);
  z-index: 1050;
}

.form-check {
  margin-bottom: 0.75rem;
}

.form-check:last-child {
  margin-bottom: 0;
}

.form-label {
  margin-bottom: 0.75rem;
  display: block;
}

.bg-light {
  background-color: #f8f9fa !important;
}
</style>